/**
 * Экран инвентаря лутбоксов
 * Показывает неоткрытые лутбоксы и позволяет их открывать
 */

import { logger } from "../../utils/logger.js";

export function createLootboxInventoryManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  documentRef = document
}) {
  
  /**
   * Загружает инвентарь лутбоксов
   */
  async function loadInventory() {
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/lootboxes/inventory`, {
        method: 'GET',
        headers: buildAuthHeaders()
      });

      if (response.success && response.data?.success) {
        return response.data.inventory || [];
      }
      
      return [];
    } catch (error) {
      logger.error('[LootboxInventory] Ошибка загрузки инвентаря:', error);
      return [];
    }
  }

  /**
   * Показывает модал с наградами
   * @param {Array} rewards - Массив наград
   * @param {string} lootboxName - Название лутбокса
   */
  function showRewardsModal(rewards, lootboxName) {
    const modal = documentRef.getElementById('rewards-modal');
    const neonBox = documentRef.getElementById('lootbox-neon-box');
    const neonContainer = documentRef.getElementById('lootbox-neon-container');
    const contentContainer = documentRef.getElementById('rewards-modal-content');
    const subtitleEl = documentRef.querySelector('[data-rewards-modal-subtitle]');
    const listEl = documentRef.querySelector('[data-rewards-modal-list]');
    
    if (!modal || !listEl) {
      logger.warn('[LootboxInventory] Элементы модала не найдены, используем fallback');
      const telegramWebApp = getTelegramWebApp();
      if (telegramWebApp?.showAlert) {
        let message = 'Лутбокс открыт! 🎉\n\nНаграды:\n';
        rewards.forEach(reward => {
          if (reward.type === 'coins') {
            message += `💰 Монеты: ${reward.amount}\n`;
          } else if (reward.type === 'stars') {
            message += `⭐ Звёзды: ${reward.amount}\n`;
          } else if (reward.type === 'energy') {
            message += `⚡ Энергия: ${reward.amount}\n`;
          } else if (reward.type === 'creature') {
            message += `🦉 Существо: ${reward.name}\n`;
          } else if (reward.type === 'exp') {
            message += `📈 Опыт: ${reward.amount}\n`;
          }
        });
        telegramWebApp.showAlert(message);
      }
      return;
    }
    
    // Показываем модал
    modal.classList.remove('hidden');
    modal.classList.add('active');
    
    // Скрываем контент с наградами, показываем анимацию
    if (contentContainer) {
      contentContainer.style.display = 'none';
    }
    if (neonContainer) {
      neonContainer.style.display = 'flex';
    }
    
    // Запускаем анимацию открытия
    if (neonBox) {
      // Сбрасываем состояние
      neonBox.classList.remove('opening', 'opened');
      
      // Небольшая задержка для плавности
      setTimeout(() => {
        neonBox.classList.add('opening');
        
        // Настраиваем частицы для взрыва
        const particles = neonBox.querySelector('.neon-particles');
        if (particles) {
          const before = particles;
          const after = particles;
          // Устанавливаем случайные направления для частиц
          const angles = [0, 45, 90, 135, 180, 225, 270, 315];
          const randomAngle = angles[Math.floor(Math.random() * angles.length)];
          const distance = 80;
          const tx = Math.cos(randomAngle * Math.PI / 180) * distance;
          const ty = Math.sin(randomAngle * Math.PI / 180) * distance;
          before.style.setProperty('--tx', `${tx}px`);
          before.style.setProperty('--ty', `${ty}px`);
        }
        
        // После анимации открытия показываем награды
        setTimeout(() => {
          neonBox.classList.remove('opening');
          neonBox.classList.add('opened');
          
          // Ещё через немного показываем контент с наградами
          setTimeout(() => {
            if (neonContainer) {
              neonContainer.style.display = 'none';
            }
            if (contentContainer) {
              contentContainer.style.display = 'block';
            }
            
            // Форматируем награды
            if (subtitleEl) {
              subtitleEl.textContent = lootboxName;
            }
            
            console.log('[LootboxInventory] Показываем награды:', rewards, 'Тип:', typeof rewards, 'Длина:', rewards?.length);
            
            // Проверяем, что награды есть
            if (!rewards || !Array.isArray(rewards) || rewards.length === 0) {
              logger.warn('[LootboxInventory] Награды пустые или не массив');
              const domCache = window.__domCacheManager;
              const errorHTML = '<div class="reward-item" style="grid-column: 1 / -1; text-align: center; padding: 20px; color: var(--error);">⚠️ Награды не получены</div>';
              domCache?.updateHTML('[data-rewards-modal-list]', errorHTML, documentRef) ||
              (listEl.innerHTML = errorHTML);
              return;
            }
            
            const rewardsHtml = rewards.map((reward, index) => {
              console.log(`[LootboxInventory] Обрабатываем награду ${index}:`, reward);
              let icon = '🎁';
              let name = 'Награда';
              let amount = '';
              let className = 'reward-item';
              
              if (reward.type === 'coins') {
                icon = '💰';
                name = 'Монеты';
                amount = reward.amount;
                className += ' coins';
              } else if (reward.type === 'stars') {
                icon = '⭐';
                name = 'Звёзды';
                amount = reward.amount;
                className += ' stars';
              } else if (reward.type === 'exp' || reward.type === 'experience') {
                icon = '📈';
                name = 'Опыт';
                amount = reward.amount;
                className += ' exp';
              } else if (reward.type === 'energy' || reward.type === 'power') {
                icon = '⚡';
                name = 'Энергия';
                amount = reward.amount;
                className += ' energy';
              } else if (reward.type === 'creature') {
                icon = '🦉';
                name = reward.name || 'Существо';
                amount = '';
                className += ' creature';
                if (reward.rarity) {
                  className += ` rarity-${reward.rarity.toLowerCase()}`;
                }
              }
              
              return `
                <div class="${className}" style="animation-delay: ${index * 0.1}s;">
                  <div class="reward-icon">${icon}</div>
                  <div class="reward-name">${name}</div>
                  ${amount ? `<div class="reward-amount">+${amount}</div>` : ''}
                  ${reward.rarity ? `<div class="reward-rarity rarity ${reward.rarity.toLowerCase()}">${reward.rarity}</div>` : ''}
                </div>
              `;
            }).filter(html => html && html.trim() !== '').join('');
            
            console.log('[LootboxInventory] Сгенерированный HTML наград (длина:', rewardsHtml.length, '):', rewardsHtml.substring(0, 200));
            
            if (!rewardsHtml || rewardsHtml.trim() === '') {
              console.error('[LootboxInventory] HTML наград пустой!');
              listEl.innerHTML = '<div class="reward-item" style="grid-column: 1 / -1; text-align: center; padding: 20px; color: var(--error);">⚠️ Ошибка отображения наград</div>';
            } else {
              listEl.innerHTML = rewardsHtml;
              console.log('[LootboxInventory] ✅ Награды успешно добавлены в DOM');
            }
          }, 500);
        }, 800);
      }, 100);
    }
    
    // Экспортируем функцию закрытия
    if (typeof window !== 'undefined') {
      window.closeRewardsModal = () => {
        modal.classList.remove('active');
        modal.classList.add('hidden');
        // Сбрасываем состояние
        if (neonBox) {
          neonBox.classList.remove('opening', 'opened');
        }
        if (contentContainer) {
          contentContainer.style.display = 'none';
        }
        if (neonContainer) {
          neonContainer.style.display = 'flex';
        }
      };
    }
  }
  
  /**
   * Открывает лутбокс из инвентаря
   */
  async function openLootboxFromInventory(lootboxId) {
    const telegramWebApp = getTelegramWebApp();
    
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/lootboxes/inventory/${lootboxId}/open`, {
        method: 'POST',
        headers: buildAuthHeaders()
      });

      console.log('[LootboxInventory] Полный ответ API:', response);
      
      // safeFetch оборачивает ответ, проверяем вложенную структуру
      let data = null;
      let rewards = [];
      let lootboxType = null;
      let userData = null;
      
      if (response.success && response.data) {
        // Проверяем вложенную структуру (response.data.data)
        if (response.data.data) {
          data = response.data.data;
          rewards = data.rewards || [];
          lootboxType = data.lootboxType;
          userData = data.user;
        } else if (response.data.success && response.data.data) {
          // Ещё один уровень вложенности
          data = response.data.data;
          rewards = data.rewards || [];
          lootboxType = data.lootboxType;
          userData = data.user;
        } else {
          // Плоская структура
          data = response.data;
          rewards = data.rewards || [];
          lootboxType = data.lootboxType;
          userData = data.user;
        }
      }
      
      console.log('[LootboxInventory] Извлечённые данные:', {
        rewards,
        lootboxType,
        userData,
        rewardsType: typeof rewards,
        isArray: Array.isArray(rewards),
        rewardsLength: rewards?.length
      });
      
      if (response.success && (data || response.data)) {
        // Обновляем баланс пользователя
        const currentUser = getCurrentUser();
        if (currentUser && userData) {
          const updatedUser = { ...currentUser, ...userData };
          setCurrentUser(updatedUser);
          updateUserBalancesUI(updatedUser);
        }
        
        // Показываем награды в красивом модале
        console.log('[LootboxInventory] Показываем награды:', rewards, 'Тип:', typeof rewards, 'Массив:', Array.isArray(rewards));
        showRewardsModal(rewards, lootboxType?.name || 'Лутбокс');
        
        // Перезагружаем инвентарь
        return true;
      } else {
        const errorMessage = response.data?.message || response.error || 'Ошибка открытия лутбокса';
        // Используем кастомное уведомление, если доступно
        if (typeof window !== 'undefined' && window.showLootboxNotification) {
          window.showLootboxNotification('❌ Ошибка', errorMessage);
        } else if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(errorMessage);
        }
        return false;
      }
    } catch (error) {
      console.error('[LootboxInventory] Ошибка открытия лутбокса:', error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Ошибка открытия лутбокса. Попробуйте позже.');
      }
      return false;
    }
  }

  /**
   * Рендерит экран инвентаря
   */
  async function renderInventory() {
    const container = documentRef.getElementById('lootbox-inventory-container');
    if (!container) return;

    const inventory = await loadInventory();
    
    if (inventory.length === 0) {
      container.innerHTML = `
        <div style="padding: 40px; text-align: center;">
          <div style="font-size: 48px; margin-bottom: 16px;">📦</div>
          <div style="font-size: 18px; margin-bottom: 8px; color: var(--text); font-weight: 600;">Инвентарь пуст</div>
          <div style="font-size: 14px; color: var(--text); opacity: 0.9;">Купите лутбоксы в магазине, чтобы они появились здесь</div>
        </div>
      `;
      return;
    }

    let html = '<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 16px; padding: 16px;">';
    
    inventory.forEach(lootbox => {
      html += `
        <div class="lootbox-inventory-item" style="
          background: var(--card);
          border: 2px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          cursor: pointer;
          transition: transform 0.2s;
        " onclick="window.openLootboxFromInventory(${lootbox.ids[0]})">
          <div style="font-size: 48px; margin-bottom: 8px;">${lootbox.icon || '📦'}</div>
          <div style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${lootbox.name}</div>
          <div style="font-size: 12px; color: var(--text-secondary); margin-bottom: 8px;">${lootbox.description || ''}</div>
          <div style="
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            color: var(--text);
            padding: 6px 12px;
            border-radius: 6px;
            font-weight: 600;
            font-size: 12px;
            box-shadow: 0 0 10px var(--glow);
          ">
            ${lootbox.count}x Открыть
          </div>
        </div>
      `;
    });
    
    html += '</div>';
    container.innerHTML = html;
  }

  return {
    loadInventory,
    openLootboxFromInventory,
    renderInventory
  };
}

